import cx from 'classnames';
import { Fragment, MouseEventHandler } from 'react';
import { DrawnMol, Mol } from '@moleculeone/types';

import { styled } from '../../../stitches.config';

import { Reagent } from './Reagent';
import { ArrowSign, PlusSign } from './Signs';
import { buildHoverCircles } from './utils/buildHoverCircles';
import { ORIENTATION_LEFT, ORIENTATION_RIGHT, PRODUCT_CLASS_NAME, SUBSTRATE_CLASS_NAME } from './utils/const';

import { ReagentCompoundRenderer } from './Reagent';
import { ArrowProps } from './Signs';
import { HoverState } from './utils/buildHoverState';
import { ReversedProductMap, ReversedSubstratesMap } from './utils/reverseMapping';

export type ReagentClassNameMapper = (substrateIdx: number) => string;

export interface ReactionOptions {
  ref: React.RefObject<HTMLDivElement>;
  alignedSubstrateMols: Mol[];
  mapToProduct: ReversedProductMap;
  mapToSubstrates: ReversedSubstratesMap;
}

export interface RendererOptions extends ReactionOptions {
  hoverState: HoverState;
}

export type SubstrateMouseEventHandler = (substrateIdx: number, event: React.MouseEvent) => void;

interface BaseReactionSVGProps {
  arrowProps?: ArrowProps;
  className?: string;
  drawnProductMol: DrawnMol;
  drawnSubstrateMols: DrawnMol[];
  interactive?: boolean;
  onProductClick?: MouseEventHandler;
  onSubstrateClick?: SubstrateMouseEventHandler;
  options: RendererOptions;
  orientation?: string;
  productMol: Mol;
  reagentClassName?: string | ReagentClassNameMapper;
  renderCompound?: ReagentCompoundRenderer;
  showSubstrates?: boolean;
  size?: number;
}

function buildReagentClassName(reagentClassName: string | ReagentClassNameMapper, index: number): string {
  if (typeof reagentClassName === 'function') {
    return reagentClassName(index);
  }

  return reagentClassName;
}

const ReactionContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

export function BaseReactionSVG(props: BaseReactionSVGProps) {
  const {
    arrowProps,
    className,
    drawnProductMol,
    drawnSubstrateMols,
    interactive = false,
    onProductClick = () => {},
    onSubstrateClick = () => {},
    options,
    orientation = ORIENTATION_LEFT,
    productMol,
    reagentClassName = '',
    renderCompound,
    showSubstrates,
    size = 0,
  } = props;
  const { ref, alignedSubstrateMols, mapToProduct, mapToSubstrates, hoverState } = options;

  if (!productMol || !alignedSubstrateMols.every(Boolean)) {
    return null;
  }

  const { productCircle, substrateCircle } = buildHoverCircles(props, {
    ref,
    alignedSubstrateMols,
    mapToProduct,
    mapToSubstrates,
    hoverState,
  });

  const product = (
    <Reagent
      className={cx(PRODUCT_CLASS_NAME, buildReagentClassName(reagentClassName, 0))}
      size={size}
      mol={productMol}
      drawnMol={drawnProductMol}
      interactive={interactive}
      circle={productCircle}
      bbox={drawnProductMol.bbox}
      index={0}
      renderCompound={renderCompound}
      onClick={onProductClick}
    />
  );

  const arrowSign = <ArrowSign {...arrowProps} size={size} orientation={orientation} />;

  return (
    <ReactionContainer className={cx(orientation, className)} css={{ height: `${size}rem` }} ref={ref}>
      {orientation === ORIENTATION_LEFT && product}

      {showSubstrates && (
        <>
          {orientation === ORIENTATION_LEFT && arrowSign}

          {drawnSubstrateMols.map((drawnMol, idx, arr) => (
            <Fragment key={idx}>
              <Reagent
                className={cx(SUBSTRATE_CLASS_NAME, buildReagentClassName(reagentClassName, idx + 1))}
                size={size}
                mol={alignedSubstrateMols[idx]}
                drawnMol={drawnMol}
                interactive={interactive}
                circle={hoverState.substrateIndex === idx && substrateCircle}
                bbox={drawnProductMol.bbox}
                index={idx + 1}
                renderCompound={renderCompound}
                onClick={event => {
                  onSubstrateClick(idx, event);
                }}
              />
              {idx < arr.length - 1 && <PlusSign size={size} />}
            </Fragment>
          ))}

          {orientation === ORIENTATION_RIGHT && arrowSign}
        </>
      )}

      {orientation === ORIENTATION_RIGHT && product}
    </ReactionContainer>
  );
}
